<template>
  <b-overlay :show="busy" variant="transparent">
    <b-alert v-if="loadError" show variant="danger">{{ loadError }}</b-alert>

    <template v-if="event">
      <b-card>
        <div class="d-flex flex-wrap justify-content-between align-items-start">
          <div class="pr-2">
            <div class="d-flex flex-wrap align-items-center mb-50">
              <h3 class="mb-0 mr-1">{{ event.title }}</h3>
              <b-badge :variant="visibilityVariant(event.visibility)" class="mr-50">
                {{ visibilityLabel(event.visibility) }}
              </b-badge>
              <b-badge variant="light-primary">{{ statusLabel(event.status) }}</b-badge>
            </div>
            <p class="text-muted mb-0">
              {{ event.organization.name }}
              <span v-if="event.city"> · {{ event.city }}</span>
              <span v-else-if="event.isOnline"> · Online</span>
            </p>
            <p class="mb-0 mt-50">
              {{ dayjs(event.startsAt).format('MMM D, YYYY h:mm A') }}
              –
              {{ dayjs(event.endsAt).format('MMM D, YYYY h:mm A') }}
            </p>
          </div>
          <div class="mt-1 mt-md-0">
            <b-button
              v-if="canHide && !event.staffHidden"
              variant="danger"
              @click="showHide = true"
            >
              <feather-icon icon="EyeOffIcon" /> Hide from public
            </b-button>
            <b-button
              v-else-if="canHide && event.staffHidden"
              variant="success"
              @click="restore()"
            >
              <feather-icon icon="EyeIcon" /> Remove staff hide
            </b-button>
          </div>
        </div>
        <b-alert v-if="event.staffHidden" show variant="warning" class="mt-1 mb-0">
          This event is hidden from the public. The organizer can still open it in their dashboard.
          New ticket sales are closed. Tickets that were already sold stay valid.
          <span v-if="event.staffHiddenNote"> Note: {{ event.staffHiddenNote }}</span>
        </b-alert>
      </b-card>

      <b-row class="match-height">
        <b-col
          v-for="card in statCards"
          :key="card.label"
          lg="3"
          md="6"
          cols="12"
        >
          <b-card>
            <div class="text-muted">{{ card.label }}</div>
            <h3 class="mb-0 mt-50">{{ card.value }}</h3>
            <small v-if="card.hint" class="text-muted">{{ card.hint }}</small>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="7">
          <b-card title="Ticket types">
            <b-table
              :items="stats.tiers || []"
              :fields="tierFields"
              responsive
              show-empty
              empty-text="No ticket types."
            >
              <template #cell(priceCents)="data">
                {{ money(data.value, event.currency) }}
              </template>
              <template #cell(sold)="data">
                {{ data.item.sold }} / {{ data.item.capacity }}
              </template>
              <template #cell(revenueCents)="data">
                {{ money(data.value, event.currency) }}
              </template>
              <template #cell(hidden)="data">
                <b-badge :variant="data.value ? 'secondary' : 'success'">
                  {{ data.value ? 'Hidden tier' : 'On sale' }}
                </b-badge>
              </template>
            </b-table>
          </b-card>
        </b-col>
        <b-col lg="5">
          <b-card title="Organization">
            <p class="mb-25 font-weight-bolder">{{ organization.name }}</p>
            <p class="mb-25 text-muted">{{ organization.country }}<span v-if="organization.verified"> · Verified</span></p>
            <p v-if="organization.email" class="mb-25">{{ organization.email }}</p>
            <p v-if="organization.website" class="mb-1">
              <a :href="organization.website" target="_blank" rel="noopener">{{ organization.website }}</a>
            </p>
            <b-button
              size="sm"
              variant="outline-primary"
              :to="{ name: 'tickets-events', query: { org: organization.name } }"
            >
              All events from this organization
            </b-button>
          </b-card>

          <b-card title="Public page">
            <p v-if="event.publicUrl" class="mb-1">
              <a :href="event.publicUrl" target="_blank" rel="noopener">{{ event.publicUrl }}</a>
            </p>
            <p v-else class="text-muted mb-0">
              Not visible to the public.
            </p>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <b-modal
      v-model="showHide"
      title="Hide event from the public"
      centered
      ok-title="Hide event"
      ok-variant="danger"
      :ok-disabled="saving"
      @ok="hide"
    >
      <p>
        Visitors will no longer see this event or be able to buy tickets.
        The organizer and Gively staff can still open it. Existing tickets stay valid.
      </p>
      <label>Note for staff (optional)</label>
      <b-form-textarea v-model="hideNote" maxlength="500" rows="3" placeholder="Why this event is being hidden" />
    </b-modal>
  </b-overlay>
</template>

<script>
import dayjs from 'dayjs'
import API from './api'
import { money, VISIBILITY_LABELS, visibilityVariant } from './format'

export default {
  name: 'TicketEvent',
  data() {
    return {
      dayjs,
      money,
      visibilityVariant,
      busy: false,
      saving: false,
      loadError: '',
      event: null,
      organization: null,
      stats: {},
      showHide: false,
      hideNote: '',
      tierFields: [
        { key: 'name', label: 'Ticket' },
        { key: 'priceCents', label: 'Price' },
        { key: 'sold', label: 'Sold' },
        { key: 'checkedIn', label: 'Checked in' },
        { key: 'revenueCents', label: 'Revenue' },
        { key: 'hidden', label: 'Tier' },
      ],
    }
  },
  computed: {
    canHide() {
      return this.$can('update', 'TicketEvents') || this.$can('manage', 'TicketEvents') || this.$can('manage', 'all')
    },
    statCards() {
      const stats = this.stats || {}
      const moneyStats = stats.money || {}
      const currency = this.event ? this.event.currency : 'CAD'
      return [
        {
          label: 'Tickets sold',
          value: stats.sold ?? '—',
          hint: `${stats.capacity ?? 0} capacity`,
        },
        {
          label: 'Orders',
          value: stats.orders ?? '—',
          hint: stats.checkedIn != null ? `${stats.checkedIn} checked in` : '',
        },
        {
          label: 'Revenue',
          value: money((moneyStats.grossCents || 0) - (moneyStats.refundsCents || 0), currency),
          hint: moneyStats.refundsCents ? `${money(moneyStats.refundsCents, currency)} refunded` : '',
        },
        {
          label: 'Page views',
          value: stats.views ?? '—',
          hint: moneyStats.donationsCents ? `${money(moneyStats.donationsCents, currency)} donations` : '',
        },
      ]
    },
  },
  created() {
    this.load()
  },
  methods: {
    visibilityLabel(value) {
      return VISIBILITY_LABELS[value] || value
    },
    statusLabel(value) {
      if (value === 'PUBLISHED') return 'Published by organizer'
      if (value === 'DRAFT') return 'Draft'
      if (value === 'CANCELLED') return 'Cancelled'
      return value
    },
    load() {
      this.busy = true
      this.loadError = ''
      API.getEvent(this.$route.params.id).then(res => {
        this.busy = false
        if (res && res.success && res.data) {
          this.apply(res.data)
          return
        }
        this.loadError = (res && res.errorMsg) || 'Could not load this event.'
      }).catch(() => {
        this.busy = false
        this.loadError = 'Could not load this event.'
      })
    },
    apply(data) {
      this.event = data.event
      this.organization = data.organization
      this.stats = data.stats || {}
    },
    hide(bvEvent) {
      bvEvent.preventDefault()
      this.saving = true
      API.setVisibility(this.event.id, { hidden: true, note: this.hideNote }).then(res => {
        this.saving = false
        if (res && res.success && res.data) {
          this.apply(res.data)
          this.showHide = false
          this.hideNote = ''
          return
        }
        this.loadError = (res && res.errorMsg) || 'Could not hide this event.'
      }).catch(() => {
        this.saving = false
        this.loadError = 'Could not hide this event.'
      })
    },
    restore() {
      this.$bvModal.msgBoxConfirm(
        'Remove the staff hide? The event returns to the visibility the organizer chose. A published public event shows on the site again. A draft or private event stays draft or private.',
        {
          title: 'Remove staff hide',
          okTitle: 'Remove hide',
          okVariant: 'success',
          cancelTitle: 'Cancel',
          centered: true,
        },
      ).then(confirmed => {
        if (!confirmed) return
        this.busy = true
        API.setVisibility(this.event.id, { hidden: false }).then(res => {
          this.busy = false
          if (res && res.success && res.data) {
            this.apply(res.data)
            return
          }
          this.loadError = (res && res.errorMsg) || 'Could not restore this event.'
        }).catch(() => {
          this.busy = false
          this.loadError = 'Could not restore this event.'
        })
      })
    },
  },
}
</script>
